import Image from 'next/image'
import { useTranslation } from 'react-i18next'
import { useFeatureFlags } from '@omnea-app/fe-org-settings/FeatureFlagsProvider'
import { useListExtOpsIntegrationsQuery } from '@omnea-app/fe-organisation-integrations/useListExtOpsIntegrations'
import { useListOrganisationIntegrations } from '@omnea-app/fe-organisation-integrations/useListOrganisationIntegrations'
import { PlusOMIcon } from '@omnea-app/icons'
import { globalIntegrationsMetadata } from '@omnea-app/packages/universal/integrations/globalIntegrations'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { CustomHttpIntegrationIcon } from '../settings/CustomHttpIntegrationIcon'
import type { MenuItemDestination, SidebarSectionItem } from '@omnea-app/fe-app-shell/sidebar/sectionItem'

const publicAPIDocsLink = process.env.NEXT_PUBLIC_API_DOCS_PATH

const integrationDestination = (integrationId: string): MenuItemDestination => ({
  kind: 'routeInstance',
  routeKey: 'settingsIntegrations',
  href: `/settings/integrations/${integrationId}`,
})

export const useIntegrationsItems = (hasPermissions: boolean): SidebarSectionItem[] => {
  const { t } = useTranslation('common', { keyPrefix: 'sidebar' })
  const { data: enabledIntegrations } = useListOrganisationIntegrations({ enabled: hasPermissions })
  const { customIntegrations } = useListExtOpsIntegrationsQuery({ enabled: hasPermissions })
  const { PUBLIC_API: isPublicApiEnabled, PUBLIC_API_UI: isPublicApiUiEnabled } = useFeatureFlags()

  if (!hasPermissions) {
    return []
  }

  const mappedIntegrations =
    enabledIntegrations?.map(({ id, integration, name }) => ({
      id,
      ...globalIntegrationsMetadata[integration],
      name,
    })) ?? []

  const enabledIntegrationsData = mappedIntegrations
    // TODO(SamC): ExtOps integrations when their settings page is built
    .filter((integration) => integration.integrationSource === 'Accounting' || integration.integrationSource === 'Native')
    // We handle the public api integration separately below
    .filter((integration) => integration.integration !== 'public-api')

  // We only want to show it in the sidebar if the feature flag is enabled, but the UI feature flag is not (because then it lives under the API settings)
  const shouldShowPublicApiIntegration = isPublicApiEnabled && !isPublicApiUiEnabled && isDefined(publicAPIDocsLink)

  // An org can have multiple public api integrations, but we only want to show one in the sidebar
  const enabledPublicApiIntegration = mappedIntegrations.find((integration) => integration.integration === 'public-api')
  const publicApiIntegration: SidebarSectionItem[] =
    shouldShowPublicApiIntegration && enabledPublicApiIntegration
      ? [
          {
            label: enabledPublicApiIntegration.title,
            icon: <Image src={enabledPublicApiIntegration.logo} alt={enabledPublicApiIntegration.title} width={16} height={16} />,
            destination: { kind: 'external', href: publicAPIDocsLink },
          },
        ]
      : []

  const customIntegrationsData = customIntegrations.filter(
    (customIntegration) =>
      customIntegration.enabled &&
      // Custom HTTP accounting integrations
      customIntegration.type === 'http' &&
      customIntegration.isAccountingIntegration
  )

  return [
    ...enabledIntegrationsData.map((integration) => ({
      label: integration.name ?? integration.title,
      icon: <Image src={integration.logo} alt={integration.title} width={16} height={16} />,
      destination: integrationDestination(integration.id),
    })),
    ...publicApiIntegration,
    ...customIntegrationsData.map((customIntegration) => ({
      label: customIntegration.name,
      icon: <CustomHttpIntegrationIcon customIntegration={customIntegration} />,
      destination: integrationDestination(customIntegration.customIntegrationId),
    })),
    {
      label: t('integrations-explore-more'),
      icon: <PlusOMIcon />,
      destination: { kind: 'route', routeKey: 'settingsIntegrations' },
    },
  ]
}
